import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { FilePreviewProvider, FilePreview, useFilePreview } from '@/common/components/FilePreview';
import { PasseriniAPI } from '@/services/apiService';
import { Loader2 } from 'lucide-react';
import type { ArchivoRecord } from '../types';
import toast from 'react-hot-toast';

interface PreviewArchivoDialogProps {
  archivo: ArchivoRecord | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const PreviewArchivoDialog: React.FC<PreviewArchivoDialogProps> = ({
  archivo,
  open,
  onOpenChange,
}) => {
  if (!archivo) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl h-[90dvh] flex flex-col p-0">
        <DialogHeader className="py-4 pl-6 pr-12 border-b">
          <DialogTitle className="truncate" title={archivo.fileName}>
            Previsualizador
          </DialogTitle>
          <DialogDescription className="sr-only">
            Vista previa del archivo {archivo.fileName}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-hidden">
          <FilePreviewProvider>
            <PreviewContent archivo={archivo} open={open} />
          </FilePreviewProvider>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Componente interno que maneja el FilePreview
const PreviewContent: React.FC<{ archivo: ArchivoRecord; open: boolean }> = ({
  archivo,
  open,
}) => {
  const { open: openPreview } = useFilePreview();
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    let isMounted = true;

    const fetchAndPreview = async () => {
      if (!open || !archivo.fileUrl) return;

      setIsLoading(true);
      setError(null);

      try {
        // Descargar archivo con autenticación
        const response = await PasseriniAPI.get(archivo.fileUrl.replace(/^\/api/, ''), {
          responseType: 'blob',
        });

        if (!isMounted) return;

        // Crear blob URL
        const blobUrl = URL.createObjectURL(response.data);

        // Abrir preview con blob URL
        openPreview(blobUrl, archivo.fileName);
      } catch (err: any) {
        console.error('Error fetching archivo for preview:', err);
        if (isMounted) {
          setError('Error al cargar el archivo para previsualización');
          toast.error('Error al cargar el archivo');
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    fetchAndPreview();

    return () => {
      isMounted = false;
    };
  }, [open, archivo.fileUrl, archivo.fileName, openPreview]);

  if (isLoading) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto text-gray-400" />
          <p className="text-sm text-gray-500 mt-2">Cargando archivo...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <div className="text-center">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      </div>
    );
  }

  return <FilePreview className="w-full h-full" />;
};
